import { useEffect, useRef } from "react"
import { Button } from "@/components/ui/button"
import { ArrowUpRight } from "lucide-react"
import { PRICING_PAGE_URL } from "@/lib/pricing-page"

/** Refus de limite de plan, tel que l'écran l'affiche. */
export type PlanLimitBannerProps = {
  /** Message du serveur : chiffres du plan de l'organisme et plan proposé. */
  detail: string
  /** Nom du plan proposé, celui de la page Tarifs (« Équipe »). */
  upgradeLabel: string
  /** Libellé de la porte de sortie : « Continuer en Gratuit » ou « Plus tard ». */
  laterLabel: string
  onUpgrade: () => void
  onLater: () => void
}

/**
 * Écran unique d'une limite de plan atteinte (réponse 402), où qu'elle
 * survienne. Garde-fous : la porte de sortie a la même taille et le même
 * poids visuel que le bouton d'upgrade ; aucun compte à rebours ni remise ;
 * seuls les chiffres du serveur pour cet organisme.
 * Le refus répond à une action de l'utilisateur.
 * Le focus entre donc dans l'écran.
 * Il revient où il était à la fermeture.
 * Sur petit écran, les boutons s'empilent à largeur égale.
 * Leur texte passe à la ligne.
 */
export function PlanLimitBanner({ detail, upgradeLabel, laterLabel, onUpgrade, onLater }: PlanLimitBannerProps) {
  const upgradeButton = useRef<HTMLButtonElement>(null)
  useEffect(() => {
    const before = document.activeElement
    if (upgradeButton.current) upgradeButton.current.focus()
    return () => {
      if (before instanceof HTMLElement && before.isConnected) before.focus()
    }
  }, [])
  return (
    <div className="space-y-3" data-test="plan-limit-banner">
      <p className="text-sm">{detail}</p>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        <Button
          ref={upgradeButton}
          type="button"
          size="sm"
          className="h-auto min-h-8 whitespace-normal text-center"
          onClick={onUpgrade}
          data-test="upgrade-cta"
        >
          Passer au plan {upgradeLabel}
          <ArrowUpRight className="ms-1 size-3.5 shrink-0" />
        </Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="h-auto min-h-8 whitespace-normal text-center"
          onClick={onLater}
          data-test="plan-limit-later"
        >
          {laterLabel}
        </Button>
      </div>
    </div>
  )
}

/**
 * PDF Preview with branding highlight and upgrade CTA.
 */
interface BrandingPreviewProps {
  children: React.ReactNode
}

export function BrandingPreview({ children }: BrandingPreviewProps) {
  return (
    <div className="relative" data-test="branding-preview">
      {children}

      {/* Highlight overlay on branding area */}
      <div className="absolute bottom-0 left-0 right-0 border-2 border-dashed border-amber-500 bg-amber-500/5 px-4 py-3 rounded-b-lg">
        <div className="flex items-center justify-between">
          <span className="text-xs text-amber-700 dark:text-amber-300">
            Branding Qualneo visible sur vos documents
          </span>
          <Button
            asChild
            size="sm"
            variant="outline"
            className="h-7 text-xs border-amber-500/50 text-amber-700"
            data-test="remove-branding-cta"
          >
            <a href={PRICING_PAGE_URL} target="_blank" rel="noopener noreferrer" className="focus:outline-none focus:ring-2 focus:ring-ring">
              Supprimer le branding
            </a>
          </Button>
        </div>
      </div>
    </div>
  )
}
